import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { FlatEvent } from "@backstory/protocol";
import type { Player } from "@backstory/player";
import { api, queryKeys, type SharedConversationSummary } from "../api";
import { Logo } from "../components/Logo";
import { PlayerView } from "../components/PlayerView";
import { ErrorState, Skeleton } from "../components/States";
import { describe } from "../components/EventList";
import { formatClock, formatDateTime, formatDuration, privacyLabel, shortUrl } from "../lib/format";
import { AnswerText } from "../features/ai/components/answer";

type InspectorTab = "events" | "console" | "network" | "askwhy" | "info";

const tabs: Array<{ id: InspectorTab; label: string }> = [
  { id: "events", label: "Events" },
  { id: "console", label: "Console" },
  { id: "network", label: "Network" },
  { id: "askwhy", label: "Ask Why" },
  { id: "info", label: "Session info" },
];

/** Keeps previously issued /share links valid while canonicalizing the viewer URL. */
export function LegacyShareRedirect() {
  const { token } = useParams({ strict: false }) as { token: string };
  const search = useSearch({ strict: false }) as { t?: number | string };
  const navigate = useNavigate();
  useEffect(() => {
    void navigate({ to: "/shared/$token", params: { token }, search: search as never, replace: true });
  }, [navigate, search.t, token]);
  return <main className="shared-state"><Logo size={24} /><Skeleton lines={2} /></main>;
}

function SharedAskWhy({ token, conversations, conversationId, onConversation, onSeek, startTs }: {
  token: string;
  conversations: SharedConversationSummary[];
  conversationId: string;
  onConversation: (id: string) => void;
  onSeek: (time: number) => void;
  startTs?: number;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const anchor = useRef<number | null>(null);
  const history = useInfiniteQuery({
    queryKey: [...queryKeys.shared(token), "conversation", conversationId],
    queryFn: ({ pageParam }) => api.sharedConversation(token, conversationId, pageParam),
    initialPageParam: "",
    getNextPageParam: (page) => page.nextCursor || undefined,
    enabled: !!conversationId,
    retry: 0,
  });
  const messages = useMemo(
    () => history.data?.pages.slice().reverse().flatMap((page) => page.messages) ?? [],
    [history.data],
  );
  useEffect(() => {
    const el = listRef.current;
    if (!el || anchor.current === null) return;
    el.scrollTop = el.scrollHeight - anchor.current;
    anchor.current = null;
  }, [messages.length]);
  const loadOlder = () => {
    if (!history.hasNextPage || history.isFetchingNextPage) return;
    const el = listRef.current;
    anchor.current = el ? el.scrollHeight - el.scrollTop : null;
    void history.fetchNextPage();
  };

  return (
    <div className="shared-chat">
      {conversations.length > 1 && (
        <label className="shared-chat-picker">
          <span>Conversation</span>
          <select value={conversationId} onChange={(event) => onConversation(event.target.value)} aria-label="Ask Why conversation" autoComplete="off">
            {conversations.map((conversation) => (
              <option key={conversation.id} value={conversation.id}>
                {conversation.preview} · {formatDateTime(conversation.updatedAt)}
              </option>
            ))}
          </select>
        </label>
      )}
      <div
        className="shared-chat-messages"
        ref={listRef}
        onScroll={(event) => {
          if (event.currentTarget.scrollTop < 48) loadOlder();
        }}
      >
        {history.hasNextPage && (
          <button className="btn ghost small" onClick={loadOlder} disabled={history.isFetchingNextPage}>
            {history.isFetchingNextPage ? "Loading…" : "Earlier messages"}
          </button>
        )}
        {history.isPending && <Skeleton lines={6} />}
        {history.isError && <ErrorState error={history.error} retry={() => void history.refetch()} />}
        {messages.map((message) => (
          <article key={message.id} className={`shared-chat-message ${message.role}`}>
            <span className="small muted">{message.role === "assistant" ? "Answer" : "Question"} · {formatDateTime(message.at)}</span>
            {message.role === "assistant" ? <AnswerText text={message.text} /> : <p>{message.text}</p>}
            {!!message.citations?.length && (
              <div className="shared-chat-citations" aria-label="Citations">
                {message.citations.map((citation, index) =>
                  citation.tStart !== undefined ? (
                    <button key={index} className="cite-chip" onClick={() => onSeek(startTs && citation.tStart! > startTs ? citation.tStart! - startTs : citation.tStart!)}>
                      {formatClock(startTs && citation.tStart > startTs ? citation.tStart - startTs : citation.tStart)}
                    </button>
                  ) : (
                    <span key={index} className="cite-chip">{citation.stepId}</span>
                  ),
                )}
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}

function SharedInspector({ events, tab, onTab, startTs, onSeek, session }: {
  events: FlatEvent[];
  tab: InspectorTab;
  onTab: (tab: InspectorTab) => void;
  startTs: number;
  onSeek: (time: number) => void;
  session: Awaited<ReturnType<typeof api.shared>>;
}) {
  const { token } = useParams({ strict: false }) as { token: string };
  const conversations = useQuery({
    queryKey: [...queryKeys.shared(token), "conversations"],
    queryFn: () => api.sharedConversations(token),
    enabled: tab === "askwhy",
    retry: 0,
  });
  const [conversationId, setConversationId] = useState("");
  useEffect(() => {
    const items = conversations.data?.items ?? [];
    if (items.length && !items.some((item) => item.id === conversationId)) setConversationId(items[0]!.id);
  }, [conversationId, conversations.data]);
  const visible = useMemo(() => {
    if (tab === "console") return events.filter((event) => event.type === "console" || event.type === "error");
    if (tab === "network") return events.filter((event) => event.type === "network");
    return events;
  }, [events, tab]);

  return (
    <aside className="shared-inspector card" aria-label="Read-only session tools">
      <div className="shared-tabs" role="tablist" aria-label="Session details">
        {tabs.map((item) => (
          <button key={item.id} role="tab" aria-selected={tab === item.id} onClick={() => onTab(item.id)}>
            {item.label}
          </button>
        ))}
      </div>
      {tab === "info" ? (
        <dl className="shared-info">
          <dt>Page</dt><dd>{session.title || shortUrl(session.url).path || "Untitled page"}</dd>
          <dt>URL</dt><dd className="mono">{session.url}</dd>
          <dt>Recorded</dt><dd>{formatDateTime(session.startedAt)}</dd>
          <dt>Duration</dt><dd>{formatDuration(session.durationMs)}</dd>
          <dt>Browser</dt><dd>{session.browser || "Not captured"}</dd>
          <dt>Operating system</dt><dd>{session.os || "Not captured"}</dd>
          <dt>Device</dt><dd>{session.device || "Not captured"}</dd>
          <dt>Viewport</dt><dd>{session.viewport.w}×{session.viewport.h} · {session.viewport.dpr}× DPR</dd>
          <dt>Privacy</dt><dd>{privacyLabel(session.privacyMode)}</dd>
          <dt>Expires</dt><dd>{formatDateTime(session.expiresAt)}</dd>
        </dl>
      ) : tab === "askwhy" ? (
        conversations.isPending ? (
          <div className="shared-inspector-state"><Skeleton lines={6} /></div>
        ) : conversations.isError ? (
          <div className="shared-inspector-state"><ErrorState error={conversations.error} retry={() => void conversations.refetch()} /></div>
        ) : conversations.data.items.length ? (
          <SharedAskWhy
            token={token}
            conversations={conversations.data.items}
            conversationId={conversationId}
            onConversation={setConversationId}
            onSeek={onSeek}
            startTs={startTs}
          />
        ) : (
          <p className="shared-inspector-state muted">No Ask Why conversations for this session.</p>
        )
      ) : (
        <ol className="shared-event-list" aria-label={`${tabs.find((item) => item.id === tab)?.label} (${visible.length})`}>
          {visible.length === 0 && <li className="muted">No {tab === "events" ? "events" : tab} captured.</li>}
          {visible.map((event, index) => (
            <li key={`${event.seq}-${event.idx}-${index}`}>
              <button onClick={() => onSeek(Math.max(0, event.ts - startTs))}>
                <span className="mono">{formatClock(event.ts - startTs)}</span>
                <span>{describe(event)}</span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}

/** Isolated, public, read-only replay reached only through an expiring capability token. */
export function SharePage() {
  const { token } = useParams({ strict: false }) as { token: string };
  const search = useSearch({ strict: false }) as { t?: number | string };
  const q = useQuery({ queryKey: queryKeys.shared(token), queryFn: () => api.shared(token), retry: 0 });
  const events = useQuery({ queryKey: [...queryKeys.shared(token), "events"], queryFn: () => api.sharedEvents(token), retry: 0 });
  const refs = useMemo(() => q.data?.chunks ?? [], [q.data?.chunks]);
  const fetchChunk = useCallback((seq: number) => api.sharedChunk(token, seq), [token]);
  const [player, setPlayer] = useState<Player | null>(null);
  const [tab, setTab] = useState<InspectorTab>("events");
  if (q.isPending) return <main className="shared-state"><Logo size={24} /><Skeleton lines={4} /></main>;
  if (q.isError) return <main className="shared-state"><div className="auth-card card"><Logo size={24} /><h1>Link unavailable</h1><ErrorState error={q.error} /><p className="muted small">The link may have expired or been revoked.</p></div></main>;
  const s = q.data;
  const page = shortUrl(s.url);
  const requestedStart = Number(search.t ?? s.startMs);
  const startAt = Number.isFinite(requestedStart) ? Math.max(0, requestedStart) : 0;
  return (
    <div className="shared-viewer">
      <header className="shared-header">
        <span className="brand"><Logo size={20} /> Backstory</span>
        <div className="shared-heading">
          <strong>{s.title || page.path || "Shared session"}</strong>
          <span className="muted small">{page.host}{page.path} · {formatDateTime(s.startedAt)}</span>
        </div>
      </header>
      <main className="shared-workspace">
        <div className="shared-player">
          <PlayerView
            refs={refs}
            fetchChunk={fetchChunk}
            privacyMode={s.privacyMode}
            readOnly
            sessionKey={token}
            onReady={(next) => {
              setPlayer(next);
              if (startAt > 0) {
                const off = next.on("loaded", () => {
                  void next.seek(startAt);
                  off();
                });
              }
            }}
          />
        </div>
        {events.isPending ? (
          <aside className="shared-inspector card"><Skeleton lines={8} /></aside>
        ) : events.isError ? (
          <aside className="shared-inspector card"><ErrorState error={events.error} retry={() => void events.refetch()} /></aside>
        ) : (
          <SharedInspector
            events={events.data.items}
            tab={tab}
            onTab={setTab}
            startTs={new Date(s.startedAt).getTime()}
            onSeek={(time) => void player?.seek(time)}
            session={s}
          />
        )}
      </main>
    </div>
  );
}
